import React, { useEffect, useRef, useState } from 'react';
import ReactApexChart from 'react-apexcharts';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { ColorRing } from 'react-loader-spinner';
import { renderToString } from 'react-dom/server';

import ChartTitle from '../../ChartTitle/ChartTitle';

import CustomYearTooltip from './CustomYearTooltip';
import CustomMonthTooltip from './CustomMonthTooltip';

import { TrapType } from 'components/AnalyticsPage/TrapType';
import { HeatMapChartData, Ranges, Series } from 'api/traps/types';
import {
  drawWeekSeparators,
  simulateCalendar,
} from 'components/DevicesDetail/MCT/TrapDetailCharts/HeatMapChart/HeatmapFunctions';
import { heatMapChartOptions } from 'components/DevicesDetail/MCT/TrapDetailCharts/HeatMapChart/chartOptions';
import useGetAnalyticsHeatMapChartData from 'hooks/Analitics/MCTCharts/6_heatmapChart/useGetAnalyticsHeatMapChartData';
import { useGetAnalyticsSRBMHeatMapChartData } from 'hooks/Analitics/SRBMCharts/useGetAnalyticsSRBMHeatMapChartData';

interface ChartProps {
  slug: string;
  type: TrapType;
}

const HeatmapChart: React.FC<ChartProps> = ({ slug, type }) => {
  const [selectedMonth, setSelectedMonth] = useState<string | null>(null);
  const [currentYear, setCurrentYear] = useState<string>(new Date().getFullYear().toString());
  const [chartHeaight, setChartHeight] = useState<number>(500);
  const isTrap = type === TrapType.ALL || type === TrapType.SRST || type === TrapType.SMST || type === TrapType.MCT;
  const isModule = type === TrapType.SRBM;

  const [tooltipData, setTooltipData] = useState<{
    seriesIndex?: any;
    dataPointIndex?: any;
    w?: any;
  } | null>(null);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });
  const chartRef = useRef<HTMLDivElement>(null);

  const { t } = useTranslation();

  let start_datetime: string | undefined;
  let end_datetime: string | undefined;
  const [isChartReady, setIsChartReady] = useState(false);
  if (selectedMonth !== null) {
    const monthStr = String(selectedMonth).padStart(2, '0');
    start_datetime = `${currentYear}-${monthStr}-01T00:00:00`;
    end_datetime = dayjs(start_datetime).endOf('month').format('YYYY-MM-DDT23:59:59');
  } else {
    start_datetime = `${currentYear}-01-01T00:00:00`;
    end_datetime = `${currentYear}-12-31T23:59:59`;
  }
  const typeForGet = () => {
    if (type === TrapType.MCT) {
      return TrapType.ALL;
    }
    return type;
  };

  const {
    data: mctData,
    refetch: mctRefetch,
    isLoading: mctIsLoading,
    isFetching: mctIsFetching,
  } = useGetAnalyticsHeatMapChartData(slug, typeForGet(), start_datetime, end_datetime, { enabled: isTrap });

  const {
    data: srbmData,
    refetch: srbmRefetch,
    isLoading: srbmIsLoading,
    isFetching: srbmIsFetching,
  } = useGetAnalyticsSRBMHeatMapChartData(slug, start_datetime, end_datetime, { enabled: isModule });

  const data = isTrap ? mctData : srbmData;

  const refetch = isTrap ? mctRefetch : srbmRefetch;

  const isLoading = isTrap ? mctIsLoading : srbmIsLoading;

  const isFetching = isTrap ? mctIsFetching : srbmIsFetching;

  const [seriesList, setSeriesList] = useState<Series[]>([]);
  const [ranges] = useState<Ranges[]>([
    { from: -1000000, to: -1000000, name: 'upcoming', color: '#9563FF' },

    { from: 0, to: 0, name: '0', color: '#8BC34A' },

    { from: 1, to: 1, name: '1', color: '#FDEfE0', caught: true },
    { from: 2, to: 2, name: '2', color: '#F9B4B4', caught: true },
    { from: 3, to: 5, name: '3-5', color: '#F27A7A', caught: true },
    { from: 6, to: 10, name: '6–10', color: '#E94B4B', caught: true },
    { from: 11, to: 20, name: '11–20', color: '#C62828', caught: true },
    { from: 21, to: 100000, name: '20+', color: '#7F1D1D', caught: true },

    { from: -1, to: -1, name: '1', color: '#FFE6C9', caught: false },
    { from: -2, to: -2, name: '2', color: '#FFC98B', caught: false },
    { from: -4, to: -3, name: '3-4', color: '#F4A24F', caught: false },
    { from: -100000, to: -5, name: '5+', color: '#E07A1F', caught: false },
  ]);

  useEffect(() => {
    setIsChartReady(false);
    refetch();
  }, [slug, type, refetch, start_datetime, end_datetime, currentYear]);

  useEffect(() => {
    if (data !== undefined) {
      const simulated = simulateCalendar(parseInt(currentYear, 10), selectedMonth, data ?? []);
      setSeriesList(simulated);
      setIsChartReady(true);
    }
  }, [currentYear, selectedMonth, data, isLoading, isFetching, slug, type]);

  const chartData: HeatMapChartData = {
    seriesList,
    ranges,
  };

  const baseChartOptions = heatMapChartOptions(
    chartData,
    type,
    setSelectedMonth,
    selectedMonth,
    currentYear,
    setChartHeight,
    t,
    selectedMonth === null || !tooltipData,
    props => {
      const row = seriesList?.[props.seriesIndex];
      const point = row?.data?.[props.dataPointIndex];

      if (row?.name === '__separator__' || point?.isSeparator) {
        return '<div></div>';
      }

      return renderToString(
        selectedMonth ? (
          <CustomMonthTooltip {...props} currentYear={currentYear} selectedMonth={selectedMonth} isSRBM={isModule} />
        ) : (
          <CustomYearTooltip {...props} currentYear={currentYear} selectedMonth={selectedMonth} isSRBM={isModule} />
        ),
      );
    },
  );

  const chartOptions = {
    ...baseChartOptions,
    chart: {
      ...baseChartOptions.chart,
      events: {
        ...baseChartOptions.chart?.events,

        mounted: function (chartContext: any) {
          drawWeekSeparators(chartContext, selectedMonth, currentYear);
        },
        updated: function (chartContext: any) {
          drawWeekSeparators(chartContext, selectedMonth, currentYear);
        },

        dataPointSelection: function (_event: any, chartContext: any, config: any) {
          if (!config || !config.w) return;

          if (selectedMonth === null) {
            setChartHeight(600);
            setSelectedMonth(String(config.seriesIndex + 1));
          } else {
            if (!chartRef.current) return;
            setTooltipData(config);
            const rect = chartRef.current.getBoundingClientRect();

            setTooltipPos({
              x: _event.clientX - rect.left,
              y: _event.clientY - rect.top,
            });
          }
        },

        dataPointMouseEnter: function (_event: any, chartContext: any, config: any) {
          if (!config || !config.w) return;

          const seriesName = String(config.w.config.series[config.seriesIndex].name).trim();
          const labels = chartContext.el.querySelectorAll('.apexcharts-yaxis-label');

          labels.forEach((label: any) => {
            const tspan = label.querySelector('tspan');
            const cleanText = (tspan ? tspan.textContent : label.textContent)?.trim();

            if (!cleanText) return;

            const isSeriesNumeric = /^\d+$/.test(seriesName);

            let isMatch = false;

            if (isSeriesNumeric) {
              const labelDayNumber = cleanText.match(/\d+$/)?.[0];
              isMatch = labelDayNumber === seriesName;
            } else {
              isMatch = cleanText === seriesName;
            }

            if (isMatch) {
              label.classList.add('active-month-label');
              if (tspan) {
                tspan.style.fill = '#9563FF';
              } else {
                label.style.fill = '#9563FF';
              }
            }
          });
        },

        dataPointMouseLeave: function (_event: any, chartContext: any, _config: any) {
          if (!chartContext || !chartContext.el) return;

          const labels = chartContext.el.querySelectorAll('.apexcharts-yaxis-label');
          labels.forEach((label: any) => {
            label.classList.remove('active-month-label');
            label.style.fill = '';

            const tspan = label.querySelector('tspan');
            if (tspan) {
              tspan.style.fill = '';
            }
          });
        },
      },
    },
  };
  const chartOptionsWithNoAnimation = {
    ...chartOptions,
    chart: {
      ...chartOptions.chart,
      animations: {
        enabled: false,
      },
    },
  };

  const showLoader = isLoading || isFetching || !isChartReady;

  return (
    <div className="card m-0 border border-2 rounded-4 position-relative h-100">
      <div className="card-header rounded-4">
        <ChartTitle
          title={t('ChartsAnalitics.heatmap_chart.title')}
          currentYear={currentYear}
          setCurrentYear={setCurrentYear}
          selectedMonth={selectedMonth}
          setSelectedMonth={setSelectedMonth}
          setChartHeight={setChartHeight}
        />
      </div>

      <div className="card-body">
        <div className="position-relative" ref={chartRef}>
          {showLoader ? (
            <div className="d-flex justify-content-center align-items-center" style={{ height: chartHeaight }}>
              <ColorRing
                visible
                height="54"
                width="54"
                ariaLabel="color-ring-loading"
                wrapperStyle={{}}
                wrapperClass="color-ring-wrapper"
                colors={['#9C7AE2', '#E27A8C', '#C0E27A', '#7AE2D0', '#F7F18F']}
              />
            </div>
          ) : (
            <>
              <ReactApexChart
                key={`${currentYear}-${selectedMonth}-${slug}-${type}`}
                options={chartOptionsWithNoAnimation}
                series={seriesList}
                type="heatmap"
                height={chartHeaight}
              />

              {tooltipData && selectedMonth && (
                <div
                  className="month-tooltip"
                  style={{
                    position: 'absolute',
                    left: tooltipPos.x,
                    top: tooltipPos.y,
                    transform: 'translate(-85%, -104%)',
                    zIndex: 9999,
                  }}
                >
                  {isModule ? (
                    <CustomMonthTooltip
                      {...tooltipData}
                      w={tooltipData.w}
                      currentYear={currentYear}
                      selectedMonth={selectedMonth}
                      isSRBM
                      onClose={() => setTooltipData(null)}
                    />
                  ) : (
                    <CustomMonthTooltip
                      {...tooltipData}
                      w={tooltipData.w}
                      currentYear={currentYear}
                      selectedMonth={selectedMonth}
                      onClose={() => setTooltipData(null)}
                    />
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default HeatmapChart;
